import { useState, useEffect, useRef, useCallback } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Circle, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { MAP_TILE_LAYERS } from '../../../services/mapTileLayers';

// ─── Asset imports ────────────────────────────────────────────────────────────
import IconLivestock      from '../../../assest/svg/Livestock.svg';
import IconMarkets        from '../../../assest/svg/Market.svg';
import IconInfrastructure from '../../../assest/svg/Infrastructure.svg';
import IconWorkforce      from '../../../assest/svg/Workforce.svg';
import IconPlanning       from '../../../assest/svg/Planning.svg';
import IconConflicts      from '../../../assest/svg/Conflict.svg';
import IconAnimalId       from '../../../assest/svg/AnimalIdentity.svg';
import IconAnimalHealth   from '../../../assest/svg/AnimalHealth.svg';

// ─────────────────────────────────────────────────────────────────────────────
// CONSTANTS
// ─────────────────────────────────────────────────────────────────────────────

const ASSET_ICONS = {
  livestock:      IconLivestock,
  markets:        IconMarkets,
  infrastructure: IconInfrastructure,
  workforce:      IconWorkforce,
  planning:       IconPlanning,
  conflicts:      IconConflicts,
  animalid:       IconAnimalId,
  animalhealth:   IconAnimalHealth,
};

// Each LGA gets a different module icon by cycling through this list
const ICON_CYCLE = [
  'conflicts', 'livestock', 'animalhealth', 'markets',
  'planning',  'workforce', 'animalid',     'infrastructure',
];

// ── Tile layers ────────────────────────────────────────────────────────────────
// Shared config (services/mapTileLayers.js) appends the CARTO API key from
// REACT_APP_CARTO_API_KEY — without it these tiles show an "API KEY REQUIRED" watermark.
// "voyager" (light) is the best match for image 2: rich colour labels, road network
// visible, proper place names, landmarks — but NOT the washed-out monochrome of "light_all".
const TILE_LAYERS = MAP_TILE_LAYERS;

// ─────────────────────────────────────────────────────────────────────────────
// STATIC DATA — all states + LGAs with full field data
// ─────────────────────────────────────────────────────────────────────────────

const NIGERIA_STATES = {
  abuja: {
    label: 'Abuja',
    center: [9.0579, 7.4951],
    zoom: 11,
    lgas: {
      'Abaji':                  { coords: [8.87,  7.18], status: 'Active',     cases: 34,  newCases: 4,  lastReport: '2 days ago',   confidence: '81%', officer: 'Dr. Halima Sule',      disease: 'Malaria'     },
      'Bwari':                  { coords: [9.29,  7.37], status: 'Monitoring', cases: 18,  newCases: 2,  lastReport: '1 day ago',    confidence: '88%', officer: 'Dr. James Okeke',      disease: 'Typhoid'     },
      'Gwagwalada':             { coords: [8.94,  7.08], status: 'Active',     cases: 61,  newCases: 8,  lastReport: '2 days ago',   confidence: '79%', officer: 'Dr. Ibrahim Musa',     disease: 'Malaria'     },
      'Kuje':                   { coords: [8.88,  7.22], status: 'Monitoring', cases: 22,  newCases: 3,  lastReport: '1 day ago',    confidence: '90%', officer: 'Dr. Sarah Nwosu',      disease: 'Malaria'     },
      'Kwali':                  { coords: [8.79,  7.02], status: 'Low Risk',   cases: 7,   newCases: 1,  lastReport: '3 days ago',   confidence: '95%', officer: 'Nurse Ada Eze',        disease: 'Dengue'      },
      'Municipal Area Council': { coords: [9.06,  7.49], status: 'Active',     cases: 128, newCases: 12, lastReport: '3 days ago',   confidence: '95%', officer: 'Dr. Amina Yusuf',      disease: 'Cholera'     },
    },
  },
  lagos: {
    label: 'Lagos',
    center: [6.5244, 3.3792],
    zoom: 11,
    lgas: {
      'Agege':          { coords: [6.62, 3.32], status: 'Active',     cases: 89,  newCases: 9,  lastReport: '1 day ago',    confidence: '86%', officer: 'Dr. Segun Bello',      disease: 'Cholera'      },
      'Alimosho':       { coords: [6.61, 3.26], status: 'Monitoring', cases: 45,  newCases: 5,  lastReport: '6 hours ago',  confidence: '91%', officer: 'Dr. Funke Adeyemi',    disease: 'Malaria'      },
      'Amuwo-Odofin':   { coords: [6.47, 3.31], status: 'Active',     cases: 67,  newCases: 7,  lastReport: '2 days ago',   confidence: '83%', officer: 'Nurse Sola Ojo',       disease: 'Typhoid'      },
      'Apapa':          { coords: [6.44, 3.37], status: 'Contained',  cases: 23,  newCases: 0,  lastReport: '4 days ago',   confidence: '97%', officer: 'Dr. Emeka Eze',        disease: 'Cholera'      },
      'Badagry':        { coords: [6.42, 2.88], status: 'Active',     cases: 76,  newCases: 9,  lastReport: '2 days ago',   confidence: '80%', officer: 'Dr. Oluwole Bello',    disease: 'Malaria'      },
      'Epe':            { coords: [6.59, 3.98], status: 'Low Risk',   cases: 11,  newCases: 1,  lastReport: '5 hours ago',  confidence: '96%', officer: 'Nurse Grace Ibe',      disease: 'Dengue'       },
      'Eti-Osa':        { coords: [6.48, 3.58], status: 'Low Risk',   cases: 14,  newCases: 2,  lastReport: '3 hours ago',  confidence: '98%', officer: 'Dr. Adaeze Okafor',    disease: 'Dengue'       },
      'Ibeju-Lekki':    { coords: [6.47, 3.77], status: 'Monitoring', cases: 28,  newCases: 3,  lastReport: '1 day ago',    confidence: '87%', officer: 'Dr. Chidi Nwosu',      disease: 'Malaria'      },
      'Ifako-Ijaiye':   { coords: [6.65, 3.30], status: 'Active',     cases: 54,  newCases: 6,  lastReport: '8 hours ago',  confidence: '84%', officer: 'Nurse Tobi Ajayi',     disease: 'Typhoid'      },
      'Ikeja':          { coords: [6.60, 3.35], status: 'Active',     cases: 203, newCases: 19, lastReport: '1 day ago',    confidence: '91%', officer: 'Dr. Tunde Adeyemi',    disease: 'Cholera'      },
      'Ikorodu':        { coords: [6.62, 3.51], status: 'Monitoring', cases: 38,  newCases: 4,  lastReport: '12 hours ago', confidence: '89%', officer: 'Dr. Musa Bello',       disease: 'Typhoid'      },
      'Kosofe':         { coords: [6.60, 3.50], status: 'Monitoring', cases: 31,  newCases: 4,  lastReport: '8 hours ago',  confidence: '89%', officer: 'Nurse Amaka Eze',      disease: 'Typhoid'      },
      'Lagos Island':   { coords: [6.45, 3.40], status: 'Active',     cases: 87,  newCases: 11, lastReport: '6 hours ago',  confidence: '94%', officer: 'Dr. Ngozi Chukwu',     disease: 'Yellow Fever' },
      'Lagos Mainland': { coords: [6.50, 3.38], status: 'Active',     cases: 72,  newCases: 8,  lastReport: '3 hours ago',  confidence: '92%', officer: 'Dr. Kola Abiodun',     disease: 'Cholera'      },
      'Mushin':         { coords: [6.53, 3.35], status: 'Active',     cases: 93,  newCases: 10, lastReport: '2 days ago',   confidence: '85%', officer: 'Nurse Bisi Lawson',    disease: 'Cholera'      },
      'Ojo':            { coords: [6.47, 3.20], status: 'Monitoring', cases: 41,  newCases: 5,  lastReport: '1 day ago',    confidence: '88%', officer: 'Dr. Yemi Adebayo',     disease: 'Malaria'      },
      'Surulere':       { coords: [6.50, 3.36], status: 'Contained',  cases: 19,  newCases: 1,  lastReport: '2 days ago',   confidence: '94%', officer: 'Dr. Olu Fadipe',       disease: 'Typhoid'      },
    },
  },
  kano: {
    label: 'Kano',
    center: [12.0022, 8.592],
    zoom: 12,
    lgas: {
      'Dala':           { coords: [12.05, 8.53], status: 'Monitoring', cases: 45,  newCases: 5,  lastReport: '4 hours ago',  confidence: '93%', officer: 'Dr. Zainab Umar',      disease: 'Cholera'     },
      'Fagge':          { coords: [12.00, 8.53], status: 'Active',     cases: 78,  newCases: 9,  lastReport: '1 day ago',    confidence: '85%', officer: 'Dr. Sadiq Musa',       disease: 'Meningitis'  },
      'Gwale':          { coords: [11.98, 8.51], status: 'Monitoring', cases: 32,  newCases: 3,  lastReport: '6 hours ago',  confidence: '90%', officer: 'Nurse Halima Lawan',   disease: 'Typhoid'     },
      'Kano Municipal': { coords: [12.00, 8.59], status: 'Active',     cases: 312, newCases: 28, lastReport: '1 day ago',    confidence: '87%', officer: 'Dr. Habibu Musa',      disease: 'Meningitis'  },
      'Nassarawa':      { coords: [12.02, 8.59], status: 'Active',     cases: 98,  newCases: 12, lastReport: '3 days ago',   confidence: '76%', officer: 'Dr. Adamu Yakubu',     disease: 'Lassa Fever' },
      'Tarauni':        { coords: [12.03, 8.56], status: 'Low Risk',   cases: 14,  newCases: 1,  lastReport: '12 hours ago', confidence: '94%', officer: 'Dr. Fatima Bello',     disease: 'Dengue'      },
      'Ungogo':         { coords: [12.07, 8.57], status: 'Monitoring', cases: 29,  newCases: 3,  lastReport: '8 hours ago',  confidence: '88%', officer: 'Nurse Aisha Sani',     disease: 'Malaria'     },
      'Kumbotso':       { coords: [12.04, 8.50], status: 'Active',     cases: 55,  newCases: 6,  lastReport: '2 days ago',   confidence: '82%', officer: 'Dr. Kabir Yusuf',      disease: 'Cholera'     },
      'Rimin Gado':     { coords: [12.19, 8.48], status: 'Low Risk',   cases: 8,   newCases: 1,  lastReport: '5 hours ago',  confidence: '96%', officer: 'Nurse Ramatu Isa',     disease: 'Malaria'     },
      'Tofa':           { coords: [11.97, 8.38], status: 'Contained',  cases: 17,  newCases: 0,  lastReport: '4 days ago',   confidence: '98%', officer: 'Dr. Musa Garba',       disease: 'Typhoid'     },
    },
  },
  rivers: {
    label: 'Rivers',
    center: [4.8156, 7.0498],
    zoom: 11,
    lgas: {
      'Port Harcourt': { coords: [4.82, 7.05], status: 'Active',     cases: 167, newCases: 15, lastReport: '2 days ago',   confidence: '88%', officer: 'Dr. Emeka Nwosu',      disease: 'Cholera'  },
      'Obio/Akpor':    { coords: [4.76, 7.08], status: 'Monitoring', cases: 39,  newCases: 6,  lastReport: '12 hours ago', confidence: '95%', officer: 'Dr. Chidinma Obi',     disease: 'Typhoid'  },
      'Okrika':        { coords: [4.73, 7.09], status: 'Active',     cases: 54,  newCases: 7,  lastReport: '1 day ago',    confidence: '82%', officer: 'Nurse Kelechi Amadi',  disease: 'Malaria'  },
      'Ogu/Bolo':      { coords: [4.73, 7.15], status: 'Low Risk',   cases: 9,   newCases: 1,  lastReport: '6 hours ago',  confidence: '93%', officer: 'Dr. Tari Erekosima',   disease: 'Dengue'   },
      'Eleme':         { coords: [4.79, 7.13], status: 'Monitoring', cases: 27,  newCases: 3,  lastReport: '8 hours ago',  confidence: '89%', officer: 'Dr. Goodnews Pepple',  disease: 'Typhoid'  },
      'Tai':           { coords: [4.73, 7.19], status: 'Active',     cases: 46,  newCases: 5,  lastReport: '2 days ago',   confidence: '84%', officer: 'Nurse Boma Dokubo',    disease: 'Malaria'  },
      'Gokana':        { coords: [4.69, 7.23], status: 'Contained',  cases: 21,  newCases: 0,  lastReport: '3 days ago',   confidence: '96%', officer: 'Dr. Felix Nwibor',     disease: 'Cholera'  },
      'Khana':         { coords: [4.62, 7.22], status: 'Active',     cases: 63,  newCases: 7,  lastReport: '1 day ago',    confidence: '81%', officer: 'Dr. Ken Badum',        disease: 'Malaria'  },
      'Bonny':         { coords: [4.44, 7.16], status: 'Monitoring', cases: 18,  newCases: 2,  lastReport: '5 hours ago',  confidence: '91%', officer: 'Nurse Ada Harry',      disease: 'Typhoid'  },
      'Degema':        { coords: [4.73, 6.79], status: 'Low Risk',   cases: 12,  newCases: 1,  lastReport: '1 day ago',    confidence: '92%', officer: 'Dr. Prince Amadi',     disease: 'Dengue'   },
    },
  },
  oyo: {
    label: 'Oyo',
    center: [7.3775, 3.947],
    zoom: 11,
    lgas: {
      'Ibadan North': { coords: [7.42, 3.91], status: 'Active',     cases: 189, newCases: 21, lastReport: '1 day ago',    confidence: '90%', officer: 'Dr. Seun Adesanya',    disease: 'Yellow Fever' },
      'Ibadan South': { coords: [7.38, 3.92], status: 'Active',     cases: 142, newCases: 16, lastReport: '6 hours ago',  confidence: '88%', officer: 'Dr. Bisi Ogunleye',    disease: 'Cholera'      },
      'Akinyele':     { coords: [7.50, 3.91], status: 'Low Risk',   cases: 27,  newCases: 3,  lastReport: '5 hours ago',  confidence: '96%', officer: 'Dr. Bola Oladele',     disease: 'Dengue'       },
      'Egbeda':       { coords: [7.37, 3.85], status: 'Active',     cases: 72,  newCases: 9,  lastReport: '3 days ago',   confidence: '84%', officer: 'Dr. Toyin Adewale',    disease: 'Cholera'      },
      'Lagelu':       { coords: [7.44, 3.99], status: 'Monitoring', cases: 31,  newCases: 4,  lastReport: '1 day ago',    confidence: '87%', officer: 'Nurse Kemi Afolabi',   disease: 'Malaria'      },
      'Ona-Ara':      { coords: [7.34, 4.02], status: 'Monitoring', cases: 24,  newCases: 3,  lastReport: '8 hours ago',  confidence: '91%', officer: 'Dr. Gbenga Akinola',   disease: 'Typhoid'      },
      'Oluyole':      { coords: [7.33, 3.89], status: 'Contained',  cases: 15,  newCases: 0,  lastReport: '4 days ago',   confidence: '97%', officer: 'Dr. Tola Adesina',     disease: 'Cholera'      },
      'Ido':          { coords: [7.44, 3.84], status: 'Low Risk',   cases: 9,   newCases: 1,  lastReport: '2 hours ago',  confidence: '95%', officer: 'Nurse Yinka Babatunde',disease: 'Dengue'       },
      'Afijio':       { coords: [7.90, 3.78], status: 'Active',     cases: 58,  newCases: 7,  lastReport: '2 days ago',   confidence: '83%', officer: 'Dr. Fola Owoade',      disease: 'Malaria'      },
      'Atisbo':       { coords: [8.57, 3.25], status: 'Monitoring', cases: 19,  newCases: 2,  lastReport: '1 day ago',    confidence: '89%', officer: 'Nurse Shade Alabi',    disease: 'Typhoid'      },
    },
  },
};

// Build flat marker array from state — every LGA gets a pin
const buildMarkers = (stateKey) =>
  Object.entries(NIGERIA_STATES[stateKey].lgas).map(([lgaName, info], idx) => ({
    id:       `${stateKey}-${idx}`,
    position: info.coords,
    iconKey:  ICON_CYCLE[idx % ICON_CYCLE.length],
    lgaKey:   lgaName,
    title:    `${lgaName} Field Post`,
    data: {
      cases:      info.cases,
      newCases:   info.newCases,
      lastReport: info.lastReport,
      confidence: info.confidence,
      status:     info.status,
      officer:    info.officer,
      disease:    info.disease,
    },
  }));

const STATE_MARKERS = Object.fromEntries(
  Object.keys(NIGERIA_STATES).map(k => [k, buildMarkers(k)])
);

const STATUS_COLOR = {
  Active:     { hex: '#ef4444', bg: 'rgba(239,68,68,0.12)'  },
  Contained:  { hex: '#22c55e', bg: 'rgba(34,197,94,0.12)'  },
  Monitoring: { hex: '#3b82f6', bg: 'rgba(59,130,246,0.12)' },
  'Low Risk': { hex: '#22c55e', bg: 'rgba(34,197,94,0.12)'  },
};

// ─────────────────────────────────────────────────────────────────────────────
// CUSTOM HOOK · useGeolocation
// Returns { position, accuracy, error, loading } and a `request` function
// ─────────────────────────────────────────────────────────────────────────────

const useGeolocation = () => {
  const [state, setState] = useState({
    position: null,   // { lat, lng }
    accuracy: null,   // metres
    error:    null,
    loading:  false,
  });

  const request = useCallback(() => {
    if (!navigator.geolocation) {
      setState(s => ({ ...s, error: 'Geolocation is not supported by this browser.' }));
      return;
    }
    setState(s => ({ ...s, loading: true, error: null }));
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setState({
          position: { lat: pos.coords.latitude, lng: pos.coords.longitude },
          accuracy: Math.round(pos.coords.accuracy),
          error:    null,
          loading:  false,
        });
      },
      (err) => {
        setState({ position: null, accuracy: null, error: err.message, loading: false });
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  }, []);

  return { ...state, request };
};

// ─────────────────────────────────────────────────────────────────────────────
// ICON BUILDERS  (inline styles required — Leaflet renders outside React DOM)
// ─────────────────────────────────────────────────────────────────────────────

const makeIcon = (iconKey, hovered = false) => {
  const src    = ASSET_ICONS[iconKey] || ASSET_ICONS.conflicts;
  const border = hovered ? '#22c55e' : '#e5e7eb';
  const bg     = hovered ? '#f0fdf4' : '#ffffff';
  const shadow = hovered
    ? '0 0 0 3px rgba(34,197,94,0.25),0 4px 14px rgba(34,197,94,0.4)'
    : '0 2px 8px rgba(0,0,0,0.25)';

  return L.divIcon({
    html: `
      <div style="width:40px;height:52px;display:flex;flex-direction:column;align-items:center;">
        <div style="width:36px;height:36px;border-radius:50%;background:${bg};border:2.5px solid ${border};
          box-shadow:${shadow};display:flex;align-items:center;justify-content:center;transition:all 0.15s ease;">
          <img src="${src}" style="width:18px;height:18px;object-fit:contain;opacity:0.82;" />
        </div>
        <div style="width:2px;height:12px;background:${border};border-radius:0 0 2px 2px;"></div>
        <div style="width:5px;height:3px;border-radius:50%;background:rgba(0,0,0,0.2);margin-top:-1px;"></div>
      </div>`,
    className: '',
    iconSize:    [40, 52],
    iconAnchor:  [20, 52],
    popupAnchor: [0, -54],
  });
};

// "You Are Here" marker — pulsing blue dot with accuracy ring
const makeYouAreHereIcon = () =>
  L.divIcon({
    html: `
      <div style="position:relative;width:28px;height:28px;">
        <!-- outer pulse ring -->
        <div style="position:absolute;inset:-8px;border-radius:50%;background:rgba(59,130,246,0.15);
          animation:yah-pulse 2s ease-out infinite;"></div>
        <!-- mid ring -->
        <div style="position:absolute;inset:-2px;border-radius:50%;background:rgba(59,130,246,0.2);
          border:2px solid rgba(59,130,246,0.4);"></div>
        <!-- core dot -->
        <div style="position:absolute;inset:4px;border-radius:50%;background:#3b82f6;
          border:2.5px solid #ffffff;box-shadow:0 2px 8px rgba(59,130,246,0.6);"></div>
        <style>
          @keyframes yah-pulse {
            0%   { transform:scale(1);   opacity:0.8; }
            70%  { transform:scale(2.2); opacity:0;   }
            100% { transform:scale(2.2); opacity:0;   }
          }
        </style>
      </div>`,
    className: '',
    iconSize:    [28, 28],
    iconAnchor:  [14, 14],
    popupAnchor: [0, -16],
  });

const makeLGAIcon = (hovered = false) => {
  const border = hovered ? '#16a34a' : '#22c55e';
  const bg     = hovered ? '#dcfce7' : '#f0fdf4';
  const shadow = hovered
    ? '0 0 0 3px rgba(34,197,94,0.3),0 4px 14px rgba(34,197,94,0.5)'
    : '0 4px 14px rgba(34,197,94,0.3)';
  return L.divIcon({
    html: `
      <div style="width:40px;height:52px;display:flex;flex-direction:column;align-items:center;">
        <div style="width:36px;height:36px;border-radius:50%;background:${bg};border:2.5px solid ${border};
          box-shadow:${shadow};display:flex;align-items:center;justify-content:center;">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="${border}" stroke-width="2.5" stroke-linecap="round">
            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/>
            <circle cx="12" cy="10" r="3"/>
          </svg>
        </div>
        <div style="width:2px;height:12px;background:${border};border-radius:0 0 2px 2px;"></div>
        <div style="width:5px;height:3px;border-radius:50%;background:rgba(0,0,0,0.18);margin-top:-1px;"></div>
      </div>`,
    className: '',
    iconSize:    [40, 52],
    iconAnchor:  [20, 52],
    popupAnchor: [0, -54],
  });
};

// ─────────────────────────────────────────────────────────────────────────────
// SECTION 1 · POPUP CONTENT  (inline styles — outside React DOM in Leaflet)
// ─────────────────────────────────────────────────────────────────────────────

const MarkerPopup = ({ marker }) => {
  const { title, data } = marker;
  const pct = parseInt(data.confidence);

  return (
    <div style={{ fontFamily: 'ui-monospace,monospace', background: '#0d1117', borderRadius: 12, minWidth: 286, overflow: 'hidden', border: '1px solid #30363d' }}>
      {/* Confidence bar */}
      <div style={{ height: 4, background: `linear-gradient(90deg,#22c55e ${pct}%,#1c2128 ${pct}%)`, borderRadius: '12px 12px 0 0' }} />
      {/* Title row */}
      <div style={{ padding: '12px 16px 10px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', borderBottom: '1px solid #21262d' }}>
        <div>
          <div style={{ fontWeight: 700, fontSize: 14, color: '#e6edf3', marginBottom: 3 }}>{title}</div>
          <div style={{ fontSize: 11, color: '#484f58' }}>Details:</div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 5, background: '#161b22', border: '1px solid #22c55e', borderRadius: 8, padding: '3px 8px', flexShrink: 0 }}>
          {/* <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg> */}
          <span style={{ color: '#22c55e', fontWeight: 700, fontSize: 13 }}>{data.confidence}</span>
        </div>
      </div>
      {/* Detail rows */}
      <div style={{ padding: '10px 16px 8px' }}>
        {[
          { label: 'Number of Cases:', value: `${data.cases} Active Cases` },
          { label: 'New Cases:',        value: `+${data.newCases} cases in the last 24 hours` },
          { label: 'Last reported:',    value: data.lastReport },
        ].map(({ label, value }) => (
          <div key={label} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 7, fontSize: 12 }}>
            <span style={{ color: '#8b949e', whiteSpace: 'nowrap', marginRight: 12 }}>• {label}</span>
            <span style={{ color: '#e6edf3', fontWeight: 500, textAlign: 'right' }}>{value}</span>
          </div>
        ))}
      </div>
      {/* Status badge */}
      <div style={{ padding: '0 16px 12px', display: 'flex', justifyContent: 'flex-end' }}>
         </div>
    </div>
  );
};

const LGAPopup = ({ lgaName, stateName }) => (
  <div style={{ fontFamily: 'ui-monospace,monospace', background: '#0d1117', borderRadius: 12, minWidth: 220, overflow: 'hidden', border: '1px solid #30363d' }}>
    <div style={{ height: 4, background: '#22c55e', borderRadius: '12px 12px 0 0' }} />
    <div style={{ padding: '12px 16px' }}>
      <div style={{ fontWeight: 700, fontSize: 14, color: '#e6edf3', marginBottom: 4 }}>{lgaName}</div>
      <div style={{ fontSize: 11, color: '#8b949e' }}>{stateName} — Local Government Area</div>
      <div style={{ marginTop: 8, fontSize: 11, color: '#484f58' }}>No active field data for this LGA</div>
    </div>
  </div>
);

// "You Are Here" popup content
const YouAreHerePopup = ({ lat, lng, accuracy }) => (
  <div style={{ fontFamily: 'ui-sans-serif,system-ui,sans-serif', background: '#0d1117', borderRadius: 12, minWidth: 220, overflow: 'hidden', border: '1px solid #30363d' }}>
    <div style={{ height: 4, background: 'linear-gradient(90deg,#3b82f6,#60a5fa)', borderRadius: '12px 12px 0 0' }} />
    <div style={{ padding: '12px 16px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#3b82f6', boxShadow: '0 0 0 3px rgba(59,130,246,0.25)', flexShrink: 0 }} />
        <span style={{ fontWeight: 700, fontSize: 13, color: '#e6edf3' }}>You Are Here</span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
        {[
          { label: 'Latitude',  value: lat.toFixed(6) },
          { label: 'Longitude', value: lng.toFixed(6) },
          { label: 'Accuracy',  value: `±${accuracy} m`},
        ].map(({ label, value }) => (
          <div key={label} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
            <span style={{ color: '#484f58' }}>{label}</span>
            <span style={{ color: '#8b949e', fontFamily: 'ui-monospace,monospace' }}>{value}</span>
          </div>
        ))}
      </div>
    </div>
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// SECTION 2 · MARKER COMPONENTS
// ─────────────────────────────────────────────────────────────────────────────

const HoverMarker = ({ marker, icons }) => {
  const ref    = useRef(null);
  const onOver = useCallback(() => ref.current?.setIcon(icons.hovered), [icons]);
  const onOut  = useCallback(() => ref.current?.setIcon(icons.default),  [icons]);
  return (
    <Marker ref={ref} position={marker.position} icon={icons.default} eventHandlers={{ mouseover: onOver, mouseout: onOut }}>
      <Popup maxWidth={320} minWidth={290} className="nlims-popup">
        <MarkerPopup marker={marker} />
      </Popup>
    </Marker>
  );
};

const LGAPlaceholderMarker = ({ position, lgaName, stateName }) => {
  const ref     = useRef(null);
  const defIcon = useRef(makeLGAIcon(false));
  const hovIcon = useRef(makeLGAIcon(true));
  const onOver  = useCallback(() => ref.current?.setIcon(hovIcon.current), []);
  const onOut   = useCallback(() => ref.current?.setIcon(defIcon.current), []);
  return (
    <Marker ref={ref} position={position} icon={defIcon.current} eventHandlers={{ mouseover: onOver, mouseout: onOut }}>
      <Popup maxWidth={260} minWidth={220} className="nlims-popup">
        <LGAPopup lgaName={lgaName} stateName={stateName} />
      </Popup>
    </Marker>
  );
};

// "You Are Here" marker — blue dot + accuracy circle
const YouAreHereMarker = ({ lat, lng, accuracy }) => {
  const icon = useRef(makeYouAreHereIcon());
  return (
    <>
      {/* Accuracy radius circle */}
      <Circle
        center={[lat, lng]}
        radius={accuracy}
        pathOptions={{ color: '#3b82f6', fillColor: '#3b82f6', fillOpacity: 0.08, weight: 1.5, dashArray: '4 4' }}
      />
      <Marker position={[lat, lng]} icon={icon.current}>
        <Popup maxWidth={260} minWidth={220} className="nlims-popup">
          <YouAreHerePopup lat={lat} lng={lng} accuracy={accuracy} />
        </Popup>
      </Marker>
    </>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// SECTION 3 · MAP BEHAVIOUR CONTROLLER
// ─────────────────────────────────────────────────────────────────────────────

const MapBehaviourController = ({ center, zoom, mapRef, activated }) => {
  const map = useMap();

  useEffect(() => { mapRef.current = map; }, [map]);

  // Scroll zoom always off — prevents accidental page-scroll hijacking
  useEffect(() => { map.scrollWheelZoom.disable(); }, [map]);

  // Toggle full interactivity on click, lock on click-outside
  useEffect(() => {
    if (activated) {
      map.dragging.enable();
      map.touchZoom.enable();
      map.doubleClickZoom.enable();
      map.keyboard.enable();
    } else {
      map.dragging.disable();
      map.touchZoom.disable();
      map.doubleClickZoom.disable();
      map.keyboard.disable();
    }
  }, [map, activated]);

  // Fly to new location whenever state or LGA changes
  useEffect(() => {
    map.flyTo(center, zoom, { duration: 1.2, easeLinearity: 0.35 });
  }, [center[0], center[1], zoom]);

  return null;
};

// ─────────────────────────────────────────────────────────────────────────────
// SECTION 4 · MAP CONTROL BUTTONS
// ─────────────────────────────────────────────────────────────────────────────

const MapControls = ({ mapTile, onToggleTile, onFullscreen, onReset, onLocate, locating }) => {
  const map = useMap();

  const btnBase = [
    'w-8 h-8 flex items-center justify-center rounded-md cursor-pointer',
    'bg-white/95 border border-black/10 text-gray-700 shadow',
    'hover:bg-green-50 hover:border-green-300 transition-colors duration-150',
  ].join(' ');

  const activeExtra = '!bg-green-50 !border-green-400 !text-green-700';

  return (
    <>
      {/* Top-right: fullscreen */}
      <div className="absolute top-2.5 right-2.5 z-[1000]">
        <button onClick={onFullscreen} title="Toggle fullscreen"
          className={`${btnBase} !w-[34px] !h-[34px] rounded-lg`}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <path d="M8 3H5a2 2 0 00-2 2v3m18 0V5a2 2 0 00-2-2h-3m0 18h3a2 2 0 002-2v-3M3 16v3a2 2 0 002 2h3"/>
          </svg>
        </button>
      </div>

      {/* Bottom-right: stacked controls */}
      <div className="absolute bottom-9 right-2.5 z-[1000] flex flex-col gap-1.5">

        {/* Locate — triggers geolocation + flies to user position */}
        <button
          onClick={onLocate}
          title="Show my location"
          className={`${btnBase} ${locating ? 'animate-pulse' : ''}`}
        >
          {locating
            ? <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2" className="animate-spin">
                <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/>
              </svg>
            : <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="3"/>
                <path d="M12 2v3m0 14v3M2 12h3m14 0h3"/>
                <circle cx="12" cy="12" r="8" strokeDasharray="2 3"/>
              </svg>
          }
        </button>

        {/* Zoom in */}
        <button onClick={() => map.zoomIn()} title="Zoom in"
          className={`${btnBase} text-xl font-light leading-none`}>+</button>

        {/* Zoom out */}
        <button onClick={() => map.zoomOut()} title="Zoom out"
          className={`${btnBase} text-2xl font-extralight leading-none`}>−</button>

        {/* Reset / refresh */}
        <button onClick={onReset} title="Reset view" className={btnBase}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <path d="M1 4v6h6"/><path d="M3.51 15a9 9 0 102.13-9.36L1 10"/>
          </svg>
        </button>

        {/* Satellite toggle */}
        <button onClick={() => onToggleTile('sat')} title={mapTile === 'satellite' ? 'Map view' : 'Satellite view'}
          className={`${btnBase} ${mapTile === 'satellite' ? activeExtra : ''}`}>
          {mapTile === 'satellite'
            ? <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18M9 21V9"/></svg>
            : <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2a10 10 0 100 20A10 10 0 0012 2z"/><path d="M2 12h20M12 2c-2.76 4-4 8-4 10s1.24 6 4 10M12 2c2.76 4 4 8 4 10s-1.24 6-4 10"/></svg>
          }
        </button>

        {/* Dark / Light (voyager) toggle */}
        <button onClick={() => onToggleTile('dark')} title={mapTile === 'dark' ? 'Switch to colour map' : 'Switch to dark map'}
          className={`${btnBase} ${mapTile === 'dark' ? activeExtra : ''}`}>
          {mapTile === 'dark'
            ? <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="5"/><path d="M12 1v2m0 18v2M4.22 4.22l1.42 1.42m12.72 12.72l1.42 1.42M1 12h2m18 0h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42"/></svg>
            : <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z"/></svg>
          }
        </button>
      </div>
    </>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// SECTION 5 · HEADER
// ─────────────────────────────────────────────────────────────────────────────

const SelectDropdown = ({ displayValue, onChange, placeholder, children }) => (
  <div className="relative inline-flex items-center bg-white border border-gray-300 rounded-lg pl-3 pr-7 py-1.5 cursor-pointer min-w-[80px] select-none">
    <span className={`text-[13px] font-medium whitespace-nowrap ${displayValue ? 'text-gray-800' : 'text-gray-400'}`}>
      {displayValue || placeholder}
    </span>
    <svg className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2">
      <polyline points="6 9 12 15 18 9"/>
    </svg>
    <select onChange={onChange} className="absolute inset-0 opacity-0 cursor-pointer w-full h-full text-[13px]">
      {children}
    </select>
  </div>
);

const MapHeader = ({ stateConfig, selectedState, selectedLGA, onStateChange, onLGAChange, updated }) => (
  <div className="bg-white border-b border-gray-100 px-5 py-3 flex items-start justify-between">
    <div>
      <h2 className="text-[15px] font-bold text-gray-900 leading-tight">Regional Map Insights</h2>
      <p className="text-[11px] text-gray-400 mt-0.5">Showing insights based on your region (auto-detect location)</p>
      <p className="text-[10px] text-gray-400 mt-px">Last updated: ({updated})</p>
    </div>
    <div className="flex items-center gap-2.5 mt-0.5">
      <SelectDropdown displayValue={stateConfig.label} onChange={e => onStateChange(e.target.value)} placeholder="State">
        {Object.entries(NIGERIA_STATES).map(([k, v]) => (
          <option key={k} value={k} selected={k === selectedState}>{v.label}</option>
        ))}
      </SelectDropdown>
      <SelectDropdown displayValue={selectedLGA} onChange={e => onLGAChange(e.target.value)} placeholder="LGA">
        <option value="">All LGAs</option>
        {Object.keys(stateConfig.lgas).map(lga => (
          <option key={lga} value={lga} selected={lga === selectedLGA}>{lga}</option>
        ))}
      </SelectDropdown>
    </div>
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// SECTION 6 · OVERLAYS
// ─────────────────────────────────────────────────────────────────────────────

const ActivationOverlay = ({ visible }) => {
  if (!visible) return null;
  return (
    <div className="absolute inset-0 z-[999] flex items-center justify-center pointer-events-none">
      <div className="bg-black/55 backdrop-blur-sm text-white text-[12px] font-medium px-4 py-2 rounded-full flex items-center gap-2 shadow-lg">
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5"/>
        </svg>
        Click map to enable interaction
      </div>
    </div>
  );
};

// Toast for geolocation errors
const GeoErrorToast = ({ message, onDismiss }) => {
  if (!message) return null;
  return (
    <div className="absolute bottom-16 left-1/2 -translate-x-1/2 z-[1001] flex items-center gap-2 bg-red-900/90 backdrop-blur-sm text-red-100 text-[12px] font-medium px-4 py-2 rounded-full shadow-lg max-w-xs text-center">
      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="12" cy="12" r="10"/><path d="M12 8v4M12 16h.01"/>
      </svg>
      <span className="truncate">{message}</span>
      <button onClick={onDismiss} className="ml-1 opacity-70 hover:opacity-100">✕</button>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// SECTION 7 · GEOLOCATION FLY-TO  (inside MapContainer so useMap works)
// When geolocation resolves, fly the map to the user's position
// ─────────────────────────────────────────────────────────────────────────────

const GeoFlyTo = ({ position }) => {
  const map = useMap();
  useEffect(() => {
    if (position) {
      map.flyTo([position.lat, position.lng], 16, { duration: 1.5, easeLinearity: 0.3 });
    }
  }, [position]);
  return null;
};

// ─────────────────────────────────────────────────────────────────────────────
// SECTION 8 · MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────────────

const RegionalMapSection = () => {
  const [selectedState, setSelectedState] = useState('abuja');
  const [selectedLGA,   setSelectedLGA]   = useState('');
  const [mapTile,       setMapTile]       = useState('light');
  const [mapActivated,  setMapActivated]  = useState(false);
  const [isFullscreen,  setIsFullscreen]  = useState(false);

  const mapRef       = useRef(null);
  const containerRef = useRef(null);

  // Geolocation hook
  const geo = useGeolocation();

  // Pre-build icon pairs once on mount
  const iconsCache = useRef({});
  useEffect(() => {
    iconsCache.current = Object.fromEntries(
      Object.keys(ASSET_ICONS).map(k => [k, { default: makeIcon(k, false), hovered: makeIcon(k, true) }])
    );
  }, []);

  // Deactivate map when user clicks outside
  useEffect(() => {
    const handler = (e) => {
      const leafletEl = containerRef.current?.querySelector('.leaflet-container');
      if (leafletEl && !leafletEl.contains(e.target)) setMapActivated(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // ── Derived values ──────────────────────────────────────────────────────────
  const stateConfig    = NIGERIA_STATES[selectedState];
  const allMarkers     = STATE_MARKERS[selectedState] ?? [];
  const lgaCoords      = selectedLGA ? stateConfig.lgas[selectedLGA]?.coords : null;
  const visibleMarkers = selectedLGA ? allMarkers.filter(m => m.lgaKey === selectedLGA) : allMarkers;
  const showLGAPin     = !!(selectedLGA && lgaCoords && visibleMarkers.length === 0);
  const mapCenter      = lgaCoords ?? stateConfig.center;
  const mapZoom        = lgaCoords ? 14 : stateConfig.zoom;
  const tileConfig     = TILE_LAYERS[mapTile] || TILE_LAYERS.dark;

  const now     = new Date();
  const updated = `${now.toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' })}, ${now.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`;

  // ── Handlers ────────────────────────────────────────────────────────────────
  const handleStateChange = (val) => { setSelectedState(val); setSelectedLGA(''); };
  const handleLGAChange   = (val) => setSelectedLGA(val);

  const handleReset = () => {
    setSelectedLGA('');
    mapRef.current?.flyTo(stateConfig.center, stateConfig.zoom, { duration: 1.2 });
  };

  const handleToggleTile = (which) => {
    if (which === 'dark') setMapTile(t => t === 'light' ? 'dark' : 'light');
    else                  setMapTile(t => t === 'satellite' ? 'light' : 'satellite');
  };

  const handleFullscreen = () => {
    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen?.();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen?.();
      setIsFullscreen(false);
    }
  };

  // Request geolocation + activate map so user can see the result
  const handleLocate = () => {
    setMapActivated(true);
    geo.request();
  };

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <section ref={containerRef} className="bg-white rounded-xl border border-gray-200 overflow-hidden font-sans">

      {/* Header */}
      <MapHeader
        stateConfig={stateConfig}
        selectedState={selectedState}
        selectedLGA={selectedLGA}
        onStateChange={handleStateChange}
        onLGAChange={handleLGAChange}
        updated={updated}
      />

      {/* Map wrapper */}
      <div
        className={`relative ${isFullscreen ? 'h-screen' : 'h-[520px]'}`}
        onClick={() => { if (!mapActivated) setMapActivated(true); }}
      >
        <ActivationOverlay visible={!mapActivated} />

        {/* Geolocation error toast */}
        <GeoErrorToast message={geo.error} onDismiss={() => geo.request()} />

        <MapContainer
          center={mapCenter}
          zoom={mapZoom}
          style={{ width: '100%', height: '100%' }}
          zoomControl={false}
          attributionControl={true}
          scrollWheelZoom={false}
          dragging={false}
        >
          {/* Tile layer */}
          <TileLayer url={tileConfig.url} attribution={tileConfig.attribution} maxZoom={19} />

          {/* Behaviour controller */}
          <MapBehaviourController
            center={mapCenter}
            zoom={mapZoom}
            mapRef={mapRef}
            activated={mapActivated}
          />

          {/* Fly to user when geolocation resolves */}
          <GeoFlyTo position={geo.position} />

          {/* "You Are Here" marker + accuracy ring */}
          {geo.position && (
            <YouAreHereMarker
              lat={geo.position.lat}
              lng={geo.position.lng}
              accuracy={geo.accuracy}
            />
          )}

          {/* LGA data markers */}
          {visibleMarkers.map(m => {
            const icons = iconsCache.current[m.iconKey];
            if (!icons) return null;
            return <HoverMarker key={m.id} marker={m} icons={icons} />;
          })}

          {/* LGA placeholder if selected LGA has no markers */}
          {showLGAPin && (
            <LGAPlaceholderMarker
              position={lgaCoords}
              lgaName={selectedLGA}
              stateName={stateConfig.label}
            />
          )}

          {/* In-map control buttons */}
          <MapControls
            mapTile={mapTile}
            onToggleTile={handleToggleTile}
            onFullscreen={handleFullscreen}
            onReset={handleReset}
            onLocate={handleLocate}
            locating={geo.loading}
          />
        </MapContainer>
      </div>

      {/* Leaflet overrides */}
      <style>{`
        .nlims-popup .leaflet-popup-content-wrapper {
          background: transparent !important; border: none !important;
          box-shadow: 0 8px 32px rgba(0,0,0,0.5) !important;
          padding: 0 !important; border-radius: 12px !important; overflow: hidden;
        }
        .nlims-popup .leaflet-popup-content  { margin: 0 !important; }
        .nlims-popup .leaflet-popup-tip-container { display: none !important; }
        .nlims-popup .leaflet-popup-close-button {
          color: #8b949e !important; top: 6px !important; right: 8px !important;
          z-index: 10; font-size: 16px !important; background: transparent !important;
        }
        .leaflet-control-zoom { display: none !important; }
        .leaflet-control-attribution {
          background: rgba(10,10,10,0.6) !important; color: #6b7280 !important;
          font-size: 10px !important; padding: 2px 8px !important;
          border-radius: 4px 0 0 0 !important;
        }
        .leaflet-control-attribution a { color: #9ca3af !important; }
        .leaflet-attribution-flag { display: none !important; }
      `}</style>
    </section>
  );
};

export default RegionalMapSection;